import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Search, RefreshCw, Target } from 'lucide-react';
import { toast } from 'sonner';
import { KeywordFormDialog, KeywordRow } from '../components/keywords/KeywordComponents';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Keywords() {
  const [keywords, setKeywords] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedClient, setSelectedClient] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingKeyword, setEditingKeyword] = useState(null);
  const [trackingKeyword, setTrackingKeyword] = useState(null);
  const [formData, setFormData] = useState({
    keyword: '',
    client_id: '',
    target_url: '',
    location: 'France',
    language: 'French',
    priority: 'medium',
    status: 'tracking'
  });

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [keywordsRes, clientsRes] = await Promise.all([
        axios.get(`${API}/keywords`, { withCredentials: true }),
        axios.get(`${API}/clients`, { withCredentials: true })
      ]);
      setKeywords(keywordsRes.data);
      setClients(clientsRes.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingKeyword) {
        await axios.put(`${API}/keywords/${editingKeyword.id}`, formData, { withCredentials: true });
      } else {
        await axios.post(`${API}/keywords`, formData, { withCredentials: true });
      }
      setDialogOpen(false);
      setEditingKeyword(null);
      resetForm();
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Une erreur est survenue");
    }
  };

  const handleEdit = (kw) => {
    setEditingKeyword(kw);
    setFormData({
      keyword: kw.keyword,
      client_id: kw.client_id,
      target_url: kw.target_url || '',
      location: kw.location || 'France',
      language: kw.language || 'French',
      priority: kw.priority || 'medium',
      status: kw.status || 'tracking'
    });
    setDialogOpen(true);
  };

  const handleTrack = async (keywordId) => {
    setTrackingKeyword(keywordId);
    try {
      const response = await axios.post(`${API}/keywords/${keywordId}/track`, {}, { withCredentials: true });
      // Show result in console for debugging
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Une erreur est survenue");
    } finally {
      setTrackingKeyword(null);
    }
  };

  const handleTrackAll = async () => {
    const keywordsToTrack = filteredKeywords.slice(0, 10); // Limit to 10 at a time
    for (const kw of keywordsToTrack) {
      setTrackingKeyword(kw.id);
      try {
        await axios.post(`${API}/keywords/${kw.id}/track`, {}, { withCredentials: true });
      } catch (e) {
      toast.error(e.response?.data?.detail || "Une erreur est survenue");
    }
    }
    setTrackingKeyword(null);
    fetchData();
  };

  const handleDelete = async (keywordId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce mot-clé ?')) return;
    try {
      await axios.delete(`${API}/keywords/${keywordId}`, { withCredentials: true });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Une erreur est survenue");
    }
  };

  const resetForm = () => {
    setEditingKeyword(null);
    setFormData({
      keyword: '',
      client_id: '',
      target_url: '',
      location: 'France',
      language: 'French',
      priority: 'medium',
      status: 'tracking'
    });
  };

  const filteredKeywords = keywords.filter(kw => {
    const matchesSearch = kw.keyword.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesClient = selectedClient === 'all' || kw.client_id === selectedClient;
    return matchesSearch && matchesClient;
  });

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.name || '-';
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="keywords-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-neutral-900">Mots-cles</h1>
            <p className="text-neutral-500 mt-1" data-testid="keywords-subtitle">
              {selectedClient !== 'all' && (() => {
                const c = clients.find(x => x.id === selectedClient);
                const names = { haloscan: 'Haloscan', semrush: 'SEMrush', ubersuggest: 'Ubersuggest', dataforseo: 'DataForSEO' };
                const label = names[c?.seo_provider] || (c?.seo_provider ? c.seo_provider : 'DataForSEO');
                return `Suivez les positions de vos mots-cles via ${label}`;
              })()}
              {selectedClient === 'all' && 'Suivez les positions de vos mots-cles (provider selon le client)'}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleTrackAll}
              disabled={trackingKeyword !== null || filteredKeywords.length === 0} data-testid="track-all-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${trackingKeyword ? 'animate-spin' : ''}`} /> Actualiser tous
            </Button>
            <KeywordFormDialog
              open={dialogOpen} onOpenChange={setDialogOpen}
              editingKeyword={editingKeyword} formData={formData} setFormData={setFormData}
              clients={clients} onSubmit={handleSubmit} onReset={resetForm}
            />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <Input placeholder="Rechercher un mot-cle..." value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" data-testid="search-keywords-input" />
          </div>
          <Select value={selectedClient} onValueChange={setSelectedClient}>
            <SelectTrigger className="w-[200px]" data-testid="filter-client-select">
              <SelectValue placeholder="Tous les clients" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les clients</SelectItem>
              {clients.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>

        <div className="bg-white border border-neutral-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center" data-testid="keywords-loading">
              <div className="w-8 h-8 border-2 border-neutral-300 border-t-[#002FA7] rounded-full animate-spin mx-auto" />
            </div>
          ) : filteredKeywords.length === 0 ? (
            <div className="p-12 text-center" data-testid="no-keywords">
              <Target className="w-12 h-12 mx-auto mb-4 text-neutral-300" />
              <p className="text-neutral-500">Aucun mot-cle trouve</p>
              <p className="text-sm text-neutral-400 mt-1">Ajoutez votre premier mot-cle pour commencer le suivi</p>
            </div>
          ) : (
            <table className="w-full data-table" data-testid="keywords-table">
              <thead>
                <tr>
                  <th>Mot-cle</th>
                  <th>Client</th>
                  <th className="text-right">Position</th>
                  <th className="text-right">Evolution</th>
                  <th className="text-right">Vol. mensuel</th>
                  <th>Priorite</th>
                  <th>Derniere verif.</th>
                  <th className="w-24"></th>
                </tr>
              </thead>
              <tbody>
                {filteredKeywords.map((kw) => (
                  <KeywordRow key={kw.id} kw={kw} clientName={getClientName(kw.client_id)}
                    trackingKeyword={trackingKeyword} onTrack={handleTrack}
                    onEdit={handleEdit} onDelete={handleDelete} />
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </Layout>
  );
}
